<script lang="ts">
  /* WHAT IS UNDER THE FINGER, and then what it says.
   *
   * The body of what used to be `Sheet.svelte`. Its hit list moved to
   * `pick.svelte.ts` — see the note there on why a registered panel cannot hold
   * state the shell may unmount.
   *
   * ═══ WHY THIS IS THE HOSTED CHART'S AND NOT SHARED ═══
   *
   * The boat's ObjectSheet answers questions this deployment cannot ask: what
   * is that ship's CPA, how long to that waypoint at current speed, how much
   * water will be under ME tonight. All of them need a vessel. What IS shared
   * is the part with no boat in it — <TideSheet>, <CurrentSheet> and <NoteSheet>
   * live in chart-ui and both hosts render the same ones, so the tide
   * arithmetic and the datum warning exist once.
   */
  import CurrentSheet from '@gennetay/chart-ui/CurrentSheet';
  import NoteSheet from '@gennetay/chart-ui/NoteSheet';
  import TideSheet from '@gennetay/chart-ui/TideSheet';
  import SectionHeader from '@gennetay/chart-ui/primitives/SectionHeader';
  import { currents } from '@gennetay/chart-layers/packs/tides/currents.svelte';
  import { tideStations } from '@gennetay/chart-layers/packs/tides/tidestations.svelte';
  import { tideStationsFeed } from '@gennetay/chart-layers/packs/tides/tidestationsfeed.svelte';
  import { picked } from '../pick.svelte';
  import { pulse } from '../pulse.svelte';

  const tideOf = (id: string) =>
    tideStations.stations.find((s) => s.code === id) ??
    tideStationsFeed.stations.find((s) => s.code === id) ??
    null;
  const cur = $derived(
    picked.chosen?.kind === 'current'
      ? (currents.stations.find((s) => s.code === picked.chosen!.id) ?? null)
      : null
  );
</script>

{#if picked.chosen === null}
  <!-- MORE THAN ONE THING under the finger. Listed with its range so the choice
       is informed, nearest first — which chartpick has already ordered,
       breaking ties by what matters rather than by what happened to be
       registered first. -->
  <SectionHeader title="{picked.hits.length} things here" />
  <ul>
    {#each picked.hits as h (h.kind + h.id)}
      <li>
        <button onclick={() => picked.choose(h)}>
          <span class="kind">{h.kind}</span>
          <span class="name">{h.label}</span>
          <span class="detail">{h.detail ?? ''}</span>
        </button>
      </li>
    {/each}
  </ul>
{:else if picked.chosen.kind === 'tide'}
  {@const st = tideOf(picked.chosen.id)}
  {#if st}<TideSheet station={st} tick={pulse.n} />{/if}
{:else if picked.chosen.kind === 'current' && cur}
  <!-- The stream sheet, with the curve the first version was missing entirely:
       it listed four numbers and no picture, so "when does it turn" could not
       be answered at a glance. -->
  <CurrentSheet station={cur} tick={pulse.n} />
{:else if picked.chosen.kind === 'note'}
  <!-- Shared, because nothing in it needs a vessel. The boat renders its own
       from sanitized HTML the plugin built; this one gets structured sections
       from the feed and so has no {@html} anywhere. -->
  <NoteSheet name={picked.chosen.label} detail={picked.noteDetail} loading={picked.noteLoading} />
{/if}

<style>
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }
  li button {
    width: 100%;
    display: grid;
    grid-template-columns: 4.5em 1fr auto;
    gap: var(--sp-4);
    align-items: baseline;
    text-align: left;
    background: transparent;
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    color: var(--ink);
    font: var(--fs-body) / var(--lh-ui) var(--mono);
    padding: var(--sp-3) var(--sp-4);
    cursor: pointer;
    /* The hit list is a menu of things you might have MEANT, so a mis-tap picks
       the wrong feature. It was 34 px in a file with no coarse-pointer block at
       all — the smallest targets on the screen, on the one control whose whole
       job is disambiguating a finger. */
    min-height: var(--h-touch);
  }
  li button:hover {
    background: var(--s-2);
  }
  .kind {
    font: var(--fs-micro) / 1.4 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
  }
  .detail {
    color: var(--sub);
  }
</style>
